import { BalanceData } from "../data/balance";
import { BUILDERS_PAGE, OfferDef } from "../data/offers";
import { PlayerProfile } from "./profile";
import { startQueued } from "./upgradeQueue";

/**
 * Builders are real money only (owner decision 2026-10-19, docs/SHOP.md §18): builders 2–5 are the offers of the
 * offers.json `builders` page, sold one after another — builder n is on sale only while the player owns n − 1 — and
 * hired through the profile when the store transaction is granted (app deals.ts), once per transaction like every
 * offer. A save that hired builders for gems before keeps them; nothing is refunded. Gems never buy a builder.
 */

/** The builders page's offers in the order they are sold (builder 2 first). */
export function builderOffers(balance: BalanceData): readonly OfferDef[] {
  const page = balance.offers.pages.find((p) => p.id === BUILDERS_PAGE);
  return page ? page.offers.filter((o) => o.builder > 0) : [];
}

/** The most builders a Commander can own: the first, plus one per builder offer (5 since 2026-10-19). */
export function maxBuilders(balance: BalanceData): number {
  return builderOffers(balance).reduce((m, o) => Math.max(m, o.builder), 1);
}

/** The builder offer on sale to a player who owns `buildersOwned` (the next builder), or null when all are owned. */
export function nextBuilderOffer(balance: BalanceData, buildersOwned: number): OfferDef | null {
  return builderOffers(balance).find((o) => o.builder === buildersOwned + 1) ?? null;
}

/** Whether a builder offer is on sale now: it hires exactly the next builder. */
export function builderOnSale(def: OfferDef, buildersOwned: number): boolean {
  return def.builder === 0 || def.builder === buildersOwned + 1;
}

/**
 * Hires builder `n` (a granted builder offer): the profile owns at least n builders afterwards, and anything queued
 * starts on the new builder now. Returns the builders added (0 when it was owned already: a second delivery of the
 * same builder pays only its extras). Deterministic; never a sim input.
 */
export function hireBuilder(profile: PlayerProfile, n: number, now: number): number {
  const cap = maxBuilders(profile.balance);
  const target = Math.min(Math.max(profile.buildersOwned, n), Math.max(cap, profile.buildersOwned));
  const added = target - profile.buildersOwned;
  if (added <= 0) return 0;
  profile.buildersOwned = target;
  startQueued(profile, now);
  return added;
}
